// El prompt de la edición en el visor: el estado, el envío y las piezas que comparten la
// barra de escritorio (`BarraDelVisor`) y la del teléfono (`EdicionEnElVisorMovil`) —
// ADR-2026-10-28-editar-en-el-visor-del-celular. Antes vivía entero adentro de
// `BarraDelVisor`; se sacó para que el celular edite con lo MISMO (adjuntos, `@`, escritor,
// cadena de versiones) sin copiar el envío.

import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { MODO_UTILIZADO_EDICION, postImageEdit } from '../../lib/imageEdit';
import { activarVersion, avisoDeBase, recortarCadena, type VersionParaEditar } from '../../lib/versionesDelVisor';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { nombreDeMencion } from '../../pages/v2/mencionesDeAdjuntos';
import { etiquetaDeAdjunto, miniaturaDeAdjunto } from '../../pages/v2/etiquetaDeAdjunto';
import { useReferenciasDeEdicion } from '../../lib/useReferenciasDeEdicion';
import type { IdDeFuente } from '../../chat-atlas/ui/ModalDeFuentes';
import { useMenusDeTexto } from '../../chat-atlas/ui/useMenusDeTexto';
import { buscarFila, type FilaEditada } from '../BarraDelVisor';

/** Lo que cuesta una edición con prompt (lo mismo que muestra la pantalla de editar). */
export const CREDITOS_POR_EDICION = 1;
/** Hasta cinco renglones; de ahí en más el texto scrollea adentro. */
export const ALTO_MAXIMO_TEXTO = 5 * 22 + 18;

/** El textarea al alto de su texto (un renglón vacío, hasta `ALTO_MAXIMO_TEXTO`). */
export function ajustarAlto(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, ALTO_MAXIMO_TEXTO)}px`;
  el.style.overflowY = el.scrollHeight > ALTO_MAXIMO_TEXTO ? 'auto' : 'hidden';
}

export type AvisoDelPrompt =
  | { tipo: 'error'; texto: string }
  | { tipo: 'regenerar'; parcial: boolean }
  /** El escritor editó desde otra versión que la que se veía. */
  | { tipo: 'base'; texto: string };

export interface OpcionesDelPrompt {
  /** La imagen que se está viendo (la que se edita). */
  imagenId: string;
  /** La cadena de versiones hasta la que se ve (`cadenaParaEditar` del visor). */
  versiones: readonly VersionParaEditar[];
  companyId: string | null;
  /** Saldo y plan: `false` corta (el llamador ya mostró su aviso). */
  antesDeEditar: () => Promise<boolean>;
  onEditando?: (editando: boolean) => void;
  onEditada: (fila: FilaEditada, origen: { instruccion: string; baseId: string }) => void;
  onEmpieza?: (trabajo: Promise<string | null>, info: { baseId: string; creditos: number }) => void;
}

export function usePromptDeEdicion({
  imagenId,
  versiones,
  companyId,
  antesDeEditar,
  onEditando,
  onEditada,
  onEmpieza,
}: OpcionesDelPrompt) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [instruccion, setInstruccion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [aviso, setAviso] = useState<AvisoDelPrompt | null>(null);
  // Lo adjuntado, y qué pestaña del selector de fuentes está abierta.
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const [fuentes, setFuentes] = useState<IdDeFuente | null>(null);
  const referenciasDelBody = useReferenciasDeEdicion();

  // Los menús de la `@` (mencionar un adjunto) y el `#` (colores), como en Redes.
  const menus = useMenusDeTexto({
    valor: instruccion,
    onCambiarValor: setInstruccion,
    adjuntos,
    textareaRef: inputRef,
  });
  // Cuando la `@` inserta una mención, el texto cambia sin pasar por `onChange`: el alto
  // se vuelve a medir acá.
  useEffect(() => {
    if (inputRef.current) ajustarAlto(inputRef.current);
  }, [instruccion]);

  // Otra imagen (otra versión): el aviso y lo adjuntado eran de la anterior.
  useEffect(() => {
    setAviso(null);
    setAdjuntos([]);
  }, [imagenId]);

  const agregarAdjunto = (a: ChatAttachment) =>
    setAdjuntos((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
  const quitarAdjunto = (id: string) => setAdjuntos((prev) => prev.filter((a) => a.id !== id));

  const enviar = async (omitirChequeoRegenerar = false) => {
    const texto = instruccion.trim();
    if (!texto || enviando || !companyId) return;
    if (!(await antesDeEditar())) return;
    setEnviando(true);
    setAviso(null);
    onEditando?.(true);
    const adjuntosDeLaTanda = adjuntos;
    try {
      // El trabajo entero —pedir la edición, encontrar la fila nueva y activarla— es UNA
      // promesa que se entrega apenas arranca (`onEmpieza`): quien prestó el visor dibuja la
      // card de "Generando" en su grilla y pone el resultado aunque el visor se cierre a
      // mitad (ADR-2026-10-06-las-ediciones-del-visor-aterrizan-en-la-grilla). `null` = el
      // backend sugirió regenerar y no hay imagen.
      let regenerar: { parcial: boolean } | null = null;
      let base: { id: string; version?: number } = { id: imagenId };
      const trabajo = (async (): Promise<FilaEditada | null> => {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user) throw new Error('No se pudo identificar al usuario.');
        const referencias = await referenciasDelBody(adjuntosDeLaTanda, companyId);
        const data = await postImageEdit(
          {
            user_id: auth.user.id,
            imagen_id: imagenId,
            instruction: texto,
            mode: 'edit_general',
            // La cadena del visor y el escritor del backend
            // (ADR-2026-10-06-editar-con-referencias-y-escritor).
            versiones: recortarCadena(versiones).map((v) => ({
              imagen_id: v.imagenId,
              ...(v.instruccion ? { instruccion: v.instruccion } : {}),
            })),
            escribir_prompt: true,
            ...(referencias.length > 0 ? { reference_images: referencias } : {}),
            ...(omitirChequeoRegenerar ? { omitir_chequeo_regenerar: true } : {}),
            modo_utilizado: MODO_UTILIZADO_EDICION.edit_general ?? 'editar_imagen',
          },
          { companyId },
        );
        if (data?.response === 'sugerir_regenerar') {
          regenerar = { parcial: data.parcial === true };
          return null;
        }
        // La IA rechazó el contenido: antes caía en "No recibimos la imagen editada". El backend manda el motivo
        // (p. ej. derechos de autor) en `error`.
        if (data?.response === 'error_moderation') {
          throw new Error(data.error || 'La imagen no pasó el filtro de contenido. Probá con otra instrucción.');
        }
        const crudo = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
        if (!crudo) throw new Error('No recibimos la imagen editada.');
        if (data.base_id) base = { id: String(data.base_id), version: data.base_version };
        const fila = await buscarFila(companyId, String(crudo));
        if (!fila?.file_name) throw new Error('No pudimos cargar la imagen editada.');
        // Cada versión del visor ya es una creación guardada (ADR-2026-10-06-panel-de-edicion-en-el-visor).
        await activarVersion(String(fila.id), companyId);
        return fila;
      })();
      onEmpieza?.(
        trabajo.then((f) => (f ? String(f.id) : null)),
        { baseId: imagenId, creditos: CREDITOS_POR_EDICION },
      );
      const fila = await trabajo;
      if (!fila) {
        setAviso({ tipo: 'regenerar', parcial: (regenerar as { parcial: boolean } | null)?.parcial === true });
        return;
      }
      setInstruccion('');
      // Lo adjuntado era para ESTA edición: la próxima arranca limpia.
      setAdjuntos((prev) => prev.filter((a) => !adjuntosDeLaTanda.some((x) => x.id === a.id)));
      if (inputRef.current) {
        inputRef.current.style.height = '';
        inputRef.current.style.overflowY = 'hidden';
      }
      setListo(true);
      window.setTimeout(() => setListo(false), 1400);
      if (base.id !== imagenId) setAviso({ tipo: 'base', texto: avisoDeBase(base.version) });
      onEditada(fila, { instruccion: texto, baseId: base.id });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error && e.message ? e.message : 'No pudimos editar la imagen. Probá de nuevo.' });
    } finally {
      setEnviando(false);
      onEditando?.(false);
    }
  };

  return {
    inputRef,
    instruccion,
    setInstruccion,
    enviando,
    listo,
    aviso,
    setAviso,
    adjuntos,
    agregarAdjunto,
    quitarAdjunto,
    fuentes,
    setFuentes,
    menus,
    enviar,
  };
}

export type PromptDeEdicion = ReturnType<typeof usePromptDeEdicion>;

/** El contenido del aviso que flota arriba del prompt (error, versión base o "regenerar"). */
export function ContenidoDelAviso({ prompt, claseBoton }: { prompt: PromptDeEdicion; claseBoton: string }) {
  const { aviso } = prompt;
  if (!aviso) return null;
  if (aviso.tipo === 'error') return <p className="text-[13px] text-white/90">{aviso.texto}</p>;
  if (aviso.tipo === 'base')
    return (
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] text-white/90">{aviso.texto}</p>
        <button type="button" onClick={() => prompt.setAviso(null)} className="shrink-0 text-[12.5px] text-white/60 hover:text-white">
          Entendido
        </button>
      </div>
    );
  return (
    <div className="flex items-start gap-3">
      <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-medium">La instrucción es poco precisa para editar</p>
        <p className="mt-1 text-[12.5px] text-white/70">
          Si el producto salió muy distinto, conviene volver a generar la imagen.
          {aviso.parcial ? ' Parte de lo que pediste sí se puede editar.' : ''} No te descontamos ninguna imagen.
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          prompt.setAviso(null);
          void prompt.enviar(true);
        }}
        className={claseBoton}
      >
        Editar igual
      </button>
    </div>
  );
}

/** Lo adjuntado, como miniaturas con su X. Se mencionan con @ por su nombre. */
export function AdjuntosDelPrompt({ prompt }: { prompt: PromptDeEdicion }) {
  const { adjuntos, quitarAdjunto } = prompt;
  if (adjuntos.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-hide">
      {adjuntos.map((a) => {
        const mini = miniaturaDeAdjunto(a);
        const nombre = nombreDeMencion(a, adjuntos);
        return (
          <span
            key={a.id}
            title={`@${nombre}`}
            className="group relative inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white/[0.08] py-1 pl-1 pr-2"
          >
            {mini ? (
              <img src={mini} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-[11px] text-white/70">{etiquetaDeAdjunto(a).slice(0, 1)}</span>
            )}
            <span className="max-w-[120px] truncate text-[12.5px] text-white/80">{nombre}</span>
            <button
              type="button"
              onClick={() => quitarAdjunto(a.id)}
              aria-label={`Quitar ${nombre}`}
              className="grid h-5 w-5 place-items-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/25 hover:text-white"
            >
              <X className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
            </button>
          </span>
        );
      })}
    </div>
  );
}
